// Turns a game's per-day points (days since first bet → turnover/bets, days
// with no activity omitted) into a cumulative % of the whole window's total,
// one entry per day 1..windowDays. A day with no row carries the previous
// cumulative value forward, so the line stays flat instead of dipping.
// Returns null when the game has no activity at all in the window: there's no
// total to take a percentage of.
export function cumulativeShare(points, metricKey, windowDays) {
  const byDay = new Map(points.map(p => [p.days_since_first_bet, Number(p[metricKey]) || 0]));
  const total = Array.from(byDay.values()).reduce((sum, v) => sum + v, 0);
  if (total <= 0) return null;

  const out = [];
  let running = 0;
  for (let day = 1; day <= windowDays; day += 1) {
    running += byDay.get(day) || 0;
    out.push(100 * running / total);
  }
  return out;
}

// Lines up each game's cumulative share by day into recharts rows:
// { day, a, b }. A game with no activity contributes null on every day.
export function mergeCurves(curvesByKey, windowDays) {
  const rows = [];
  for (let i = 0; i < windowDays; i += 1) {
    const row = { day: i + 1 };
    Object.entries(curvesByKey).forEach(([key, curve]) => {
      row[key] = curve ? curve[i] : null;
    });
    rows.push(row);
  }
  return rows;
}

// Stickiness score from a cumulativeShare curve — the same definition the
// backend uses for the Stickiness report (domain.SpendStickiness):
// 100 - 200 * mean over days of |C(d) - d/window|, with C as a 0-1 share.
// 100 = spend spread evenly (the y = x diagonal); ~1.7 = all on one day.
export function stickinessScore(curve) {
  if (!curve || curve.length === 0) return null;
  const n = curve.length;
  const meanGap = curve.reduce((sum, pct, i) => sum + Math.abs(pct / 100 - (i + 1) / n), 0) / n;
  return 100 - 200 * meanGap;
}

// One row per day fromDay..toDay with active and first-return player counts,
// 0 for days with no row. The *_pct fields are each count as % of the Day-0
// cohort (everyone who made their first bet on the game), so neither can
// exceed 100%: a player active on day N is always part of that cohort, even
// if they skipped the days in between.
export function returnDays(points, fromDay, toDay, cohortPlayers) {
  const byDay = new Map(points.map(p => [p.days_since_first_bet, p]));
  const pctOf = v => (cohortPlayers > 0 ? 100 * v / cohortPlayers : null);
  return Array.from({ length: Math.max(0, toDay - fromDay + 1) }, (_, i) => {
    const day = fromDay + i;
    const p = byDay.get(day);
    const players = p?.players || 0;
    const firstReturnPlayers = p?.first_return_players || 0;
    return {
      day,
      players,
      first_return_players: firstReturnPlayers,
      players_pct: pctOf(players),
      first_return_pct: pctOf(firstReturnPlayers),
    };
  });
}

// Several games' returnDays side by side for one series ('players' or
// 'first_return_players'). curvesByKey maps each game's key to
// { points, cohortPlayers }. One row per day with each game's % of its own
// Day-0 cohort under its key and the raw count under `${key}_n`.
export function mergeReturnDays(curvesByKey, fromDay, toDay, seriesKey) {
  const pctKey = seriesKey === 'players' ? 'players_pct' : 'first_return_pct';
  const perGame = Object.entries(curvesByKey).map(([key, c]) => (
    [key, returnDays(c.points, fromDay, toDay, c.cohortPlayers)]
  ));
  return Array.from({ length: Math.max(0, toDay - fromDay + 1) }, (_, i) => {
    const row = { day: fromDay + i };
    perGame.forEach(([key, rows]) => {
      row[key] = rows[i][pctKey];
      row[`${key}_n`] = rows[i][seriesKey];
    });
    return row;
  });
}

// Games with no players in the curve's cohort: nobody's first bet on them was
// before the cohort cutoff, i.e. they're younger than the curve's window —
// not games nobody returned to.
export function tooNewGames(games, cohortByKey) {
  return games.filter(g => !(cohortByKey[g.key] > 0));
}

// Why the chart is empty: every game too new for the window, or (with a
// cohort) genuinely no returning play after day 0.
export function emptyCurveMessage(games, cohortByKey, windowDays, cohortCutoff) {
  const tooNew = tooNewGames(games, cohortByKey);
  if (games.length > 0 && tooNew.length === games.length) {
    const subject = games.length === 1 ? 'This game is' : 'These games are';
    return `${subject} too new for the stickiness curve: it only counts players whose first bet was before ${cohortCutoff}, `
      + `so everyone has a full ${windowDays} days of data. It fills in once the game is ${windowDays} days old.`;
  }
  return 'No returning play after the first day for this game';
}
